// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a small Outlook-style rich text editor for mail. A contentEditable
// surface plus a formatting toolbar; the value is HTML. Kept dependency-free
// (execCommand) because the mail body only needs the basics — bold, lists,
// links, quotes — and Graph/Outlook renders that HTML as-is.

import styles from '@app/features/konvo_mail/components/MailComposeModal.module.css';
import {
	EraserIcon,
	HighlighterIcon,
	ImageIcon,
	LinkIcon,
	ListBulletsIcon,
	ListNumbersIcon,
	QuotesIcon,
	TextAlignCenterIcon,
	TextAlignLeftIcon,
	TextAlignRightIcon,
	TextBIcon,
	TextColorIcon,
	TextIndentIcon,
	TextItalicIcon,
	TextOutdentIcon,
	TextStrikethroughIcon,
	TextUnderlineIcon,
} from '@app/features/ui/icons/Icons';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

const BOLD_DESCRIPTOR = msg({message: 'Bold', comment: 'Mail editor toolbar button.'});
const ITALIC_DESCRIPTOR = msg({message: 'Italic', comment: 'Mail editor toolbar button.'});
const UNDERLINE_DESCRIPTOR = msg({message: 'Underline', comment: 'Mail editor toolbar button.'});
const STRIKE_DESCRIPTOR = msg({message: 'Strikethrough', comment: 'Mail editor toolbar button.'});
const BULLETS_DESCRIPTOR = msg({message: 'Bulleted list', comment: 'Mail editor toolbar button.'});
const NUMBERS_DESCRIPTOR = msg({message: 'Numbered list', comment: 'Mail editor toolbar button.'});
const INDENT_DESCRIPTOR = msg({message: 'Increase indent', comment: 'Mail editor toolbar button.'});
const OUTDENT_DESCRIPTOR = msg({message: 'Decrease indent', comment: 'Mail editor toolbar button.'});
const ALIGN_LEFT_DESCRIPTOR = msg({message: 'Align left', comment: 'Mail editor toolbar button.'});
const ALIGN_CENTER_DESCRIPTOR = msg({message: 'Align centre', comment: 'Mail editor toolbar button.'});
const ALIGN_RIGHT_DESCRIPTOR = msg({message: 'Align right', comment: 'Mail editor toolbar button.'});
const QUOTE_DESCRIPTOR = msg({message: 'Quote', comment: 'Mail editor toolbar button.'});
const LINK_DESCRIPTOR = msg({message: 'Insert link', comment: 'Mail editor toolbar button.'});
const CLEAR_DESCRIPTOR = msg({message: 'Clear formatting', comment: 'Mail editor toolbar button.'});
const FONT_SIZE_DESCRIPTOR = msg({message: 'Font size', comment: 'Mail editor toolbar select label.'});
const LINK_PLACEHOLDER_DESCRIPTOR = msg({message: 'https://…', comment: 'Mail editor link URL placeholder.'});
const LINK_APPLY_DESCRIPTOR = msg({message: 'Apply', comment: 'Mail editor link popover confirm button.'});
const LINK_REMOVE_DESCRIPTOR = msg({message: 'Remove link', comment: 'Mail editor link popover button.'});
const TEXT_COLOUR_DESCRIPTOR = msg({message: 'Text colour', comment: 'Mail editor toolbar button.'});
const HIGHLIGHT_DESCRIPTOR = msg({message: 'Highlight', comment: 'Mail editor toolbar button.'});
const NO_COLOUR_DESCRIPTOR = msg({
	message: 'No colour',
	comment: 'Mail editor colour popover option that removes the text colour or highlight.',
});
const IMAGE_DESCRIPTOR = msg({message: 'Insert picture', comment: 'Mail editor toolbar button.'});
const IMAGE_TOO_LARGE_DESCRIPTOR = msg({
	message: 'Pictures in the message must be under 2 MB.',
	comment: 'Mail editor notice when an inserted or pasted picture is too big.',
});

/** Pictures in the body travel as inline attachments; Graph takes each one in a single request. */
export const MAX_INLINE_IMAGE_BYTES = 2 * 1024 * 1024;

// A restrained palette: ink, greys and a few muted hues, plus soft highlighter tints.
const TEXT_COLOURS = ['#1d1e22', '#6b6c72', '#b42318', '#b54708', '#067647', '#175cd3', '#6941c6'];
const HIGHLIGHT_COLOURS = ['#fff2a8', '#d3f8df', '#d1e9ff', '#fce7f6', '#fde2d0', '#ebe9fe'];

const FONT_SIZES: Array<{value: string; label: string}> = [
	{value: '1', label: 'Tiny'},
	{value: '2', label: 'Small'},
	{value: '3', label: 'Normal'},
	{value: '4', label: 'Medium'},
	{value: '5', label: 'Large'},
	{value: '6', label: 'Huge'},
];

export interface MailRichTextEditorProps {
	value: string;
	onChange: (html: string) => void;
	placeholder?: string;
	autoFocus?: boolean;
	minHeight?: number;
	/** Mobile sheet: drop the frame and let the writing area fill the screen. */
	chromeless?: boolean;
	/** Outlook's phone composer keeps the formatting bar under the text, above the keyboard. */
	toolbarPlacement?: 'top' | 'bottom';
	/** Lets the phone composer hide the formatting bar until it is asked for. */
	showToolbar?: boolean;
	/** Picture button and pasted pictures, inlined as data URLs (the server turns them into cid attachments). */
	allowImages?: boolean;
	/** Extra class for the toolbar row, so a host can restyle it (the docked composer's soft bar). */
	toolbarClassName?: string;
	/** Extra class for the writing surface. */
	surfaceClassName?: string;
	/**
	 * KONVO action chips: something drawn over the writing surface (the chip above
	 * a sentence, G2). It gets the surface and the box it is positioned in; any
	 * marker it puts on the surface's blocks is stripped from the HTML.
	 */
	overlay?: (parts: {editor: HTMLDivElement; stage: HTMLDivElement}) => React.ReactNode;
}

/** Attributes the editor's decorations (the action chip's gap) put on blocks; never saved or sent. */
const DECORATION_ATTRIBUTES = /\sdata-konvo-gap(?:="[^"]*")?/g;

/** The editor's HTML without its decorations, as it is saved, autosaved and sent. */
export function stripEditorDecorations(html: string): string {
	return html.includes('data-konvo-') ? html.replace(DECORATION_ATTRIBUTES, '') : html;
}

const INLINE_IMAGE_RE = /^data:image\/(png|jpe?g|gif|webp);base64,/i;

function readFileAsDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(reader.error);
		reader.onload = () => resolve(String(reader.result ?? ''));
		reader.readAsDataURL(file);
	});
}

const isEmptyHtml = (element: HTMLElement): boolean =>
	element.textContent?.trim().length === 0 && !element.querySelector('img, hr, table');

/** Strips scripts, event handlers and javascript: URLs from composed HTML before it leaves the client. */
export function sanitizeComposedHtml(html: string): string {
	if (typeof DOMParser === 'undefined') return html;
	const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
	for (const node of doc.body.querySelectorAll(
		'script, style, iframe, object, embed, form, input, textarea, button, meta, link',
	)) {
		node.remove();
	}
	for (const element of doc.body.querySelectorAll('*')) {
		for (const attribute of Array.from(element.attributes)) {
			const name = attribute.name.toLowerCase();
			const value = attribute.value.trim().toLowerCase();
			// Pictures may stay inline as data URLs (signature logos, pasted images); nothing else may.
			const inlineImage = name === 'src' && element.tagName === 'IMG' && INLINE_IMAGE_RE.test(value);
			// Editor decorations (the action chip's gap marker) never leave the client.
			if (name === 'contenteditable' || name.startsWith('data-konvo-')) {
				element.removeAttribute(attribute.name);
				continue;
			}
			if (
				name.startsWith('on') ||
				((name === 'href' || name === 'src') && /^(javascript|data|vbscript):/.test(value) && !inlineImage)
			) {
				element.removeAttribute(attribute.name);
			}
		}
	}
	return doc.body.innerHTML;
}

/** True when the HTML has no visible content (only whitespace / empty blocks). */
export function isBlankHtml(html: string): boolean {
	if (typeof DOMParser === 'undefined') return html.trim().length === 0;
	const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
	return isEmptyHtml(doc.body);
}

function normalizeUrl(raw: string): string | null {
	const text = raw.trim();
	if (!text) return null;
	if (/^(https?|mailto|tel):/i.test(text)) return text;
	if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) return `mailto:${text}`;
	if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(text)) return `https://${text}`;
	return null;
}

export const MailRichTextEditor: React.FC<MailRichTextEditorProps> = ({
	value,
	onChange,
	placeholder,
	autoFocus,
	minHeight = 220,
	chromeless = false,
	toolbarPlacement = 'top',
	showToolbar = true,
	allowImages = false,
	toolbarClassName,
	surfaceClassName,
	overlay,
}) => {
	const {i18n} = useLingui();
	const editorRef = useRef<HTMLDivElement | null>(null);
	const lastEmitted = useRef<string>('');
	const savedRange = useRef<Range | null>(null);
	const [empty, setEmpty] = useState(true);
	const [active, setActive] = useState<Record<string, boolean>>({});
	const [linkOpen, setLinkOpen] = useState(false);
	const [linkUrl, setLinkUrl] = useState('');
	const linkInputRef = useRef<HTMLInputElement | null>(null);
	const [colourMode, setColourMode] = useState<'foreColor' | 'hiliteColor' | null>(null);
	const [imageError, setImageError] = useState(false);
	const [stage, setStage] = useState<HTMLDivElement | null>(null);
	const [editorElement, setEditorElement] = useState<HTMLDivElement | null>(null);
	const setEditorRef = useCallback((element: HTMLDivElement | null) => {
		editorRef.current = element;
		setEditorElement(element);
	}, []);
	const imageInputRef = useRef<HTMLInputElement | null>(null);

	// Only push external value into the DOM when it differs from what we last emitted
	// (initial mount, or a parent reset); otherwise typing would lose the caret.
	useEffect(() => {
		const element = editorRef.current;
		if (!element || value === lastEmitted.current) return;
		element.innerHTML = value;
		lastEmitted.current = value;
		setEmpty(isEmptyHtml(element));
	}, [value]);

	useEffect(() => {
		if (autoFocus) editorRef.current?.focus();
	}, [autoFocus]);

	const emit = useCallback(() => {
		const element = editorRef.current;
		if (!element) return;
		const html = stripEditorDecorations(element.innerHTML);
		lastEmitted.current = html;
		setEmpty(isEmptyHtml(element));
		onChange(html);
	}, [onChange]);

	const refreshActive = useCallback(() => {
		const element = editorRef.current;
		if (!element || !element.contains(document.activeElement) || typeof document.queryCommandState !== 'function')
			return;
		const next: Record<string, boolean> = {};
		for (const command of [
			'bold',
			'italic',
			'underline',
			'strikeThrough',
			'insertUnorderedList',
			'insertOrderedList',
		]) {
			try {
				next[command] = document.queryCommandState(command);
			} catch {
				next[command] = false;
			}
		}
		setActive(next);
	}, []);

	useEffect(() => {
		document.addEventListener('selectionchange', refreshActive);
		return () => document.removeEventListener('selectionchange', refreshActive);
	}, [refreshActive]);

	const exec = useCallback(
		(command: string, argument?: string) => {
			const element = editorRef.current;
			if (!element) return;
			element.focus();
			try {
				document.execCommand(command, false, argument);
			} catch {
				// unsupported command in this browser; ignore
			}
			emit();
			refreshActive();
		},
		[emit, refreshActive],
	);

	const saveSelection = useCallback(() => {
		const selection = window.getSelection();
		const element = editorRef.current;
		if (!selection || selection.rangeCount === 0 || !element) {
			savedRange.current = null;
			return;
		}
		const range = selection.getRangeAt(0);
		savedRange.current = element.contains(range.commonAncestorContainer) ? range.cloneRange() : null;
	}, []);

	const restoreSelection = useCallback(() => {
		const element = editorRef.current;
		if (!element) return;
		element.focus();
		const range = savedRange.current;
		if (!range) return;
		const selection = window.getSelection();
		selection?.removeAllRanges();
		selection?.addRange(range);
	}, []);

	const openLink = useCallback(() => {
		saveSelection();
		const selection = window.getSelection();
		const anchor = selection?.anchorNode
			? (selection.anchorNode instanceof Element ? selection.anchorNode : selection.anchorNode.parentElement)?.closest(
					'a',
				)
			: null;
		setLinkUrl(anchor?.getAttribute('href') ?? '');
		setLinkOpen(true);
		window.setTimeout(() => linkInputRef.current?.focus(), 0);
	}, [saveSelection]);

	const applyLink = useCallback(() => {
		const url = normalizeUrl(linkUrl);
		setLinkOpen(false);
		if (!url) return;
		restoreSelection();
		const range = savedRange.current;
		if (!range || range.collapsed) {
			const label = linkUrl.trim();
			const escaped = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
			exec('insertHTML', `<a href="${url.replace(/"/g, '&quot;')}">${escaped}</a>&nbsp;`);
		} else {
			exec('createLink', url);
		}
		setLinkUrl('');
	}, [exec, linkUrl, restoreSelection]);

	const removeLink = useCallback(() => {
		setLinkOpen(false);
		restoreSelection();
		exec('unlink');
		setLinkUrl('');
	}, [exec, restoreSelection]);

	const openColours = useCallback(
		(mode: 'foreColor' | 'hiliteColor') => {
			saveSelection();
			setLinkOpen(false);
			setColourMode((current) => (current === mode ? null : mode));
		},
		[saveSelection],
	);

	const applyColour = useCallback(
		(colour: string | null) => {
			const mode = colourMode;
			setColourMode(null);
			if (!mode) return;
			restoreSelection();
			try {
				document.execCommand('styleWithCSS', false, 'true');
			} catch {
				// older engines ignore it and fall back to <font color>
			}
			if (colour) exec(mode, colour);
			else exec(mode, mode === 'foreColor' ? 'inherit' : 'transparent');
		},
		[colourMode, exec, restoreSelection],
	);

	const insertImages = useCallback(
		async (files: Array<File>) => {
			const images = files.filter((file) => /^image\/(png|jpe?g|gif|webp)$/i.test(file.type));
			if (images.length === 0) return;
			let tooLarge = false;
			const tags: Array<string> = [];
			for (const file of images) {
				if (file.size > MAX_INLINE_IMAGE_BYTES) {
					tooLarge = true;
					continue;
				}
				const url = await readFileAsDataUrl(file);
				if (!INLINE_IMAGE_RE.test(url)) continue;
				tags.push(`<img src="${url}" alt="" style="max-width:100%;height:auto">`);
			}
			setImageError(tooLarge);
			if (tags.length === 0) return;
			restoreSelection();
			exec('insertHTML', tags.join(''));
		},
		[exec, restoreSelection],
	);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === 'k') {
			event.preventDefault();
			openLink();
		}
	};

	const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
		const pastedImages = allowImages
			? Array.from(event.clipboardData.files).filter((file) => file.type.startsWith('image/'))
			: [];
		if (pastedImages.length > 0) {
			event.preventDefault();
			saveSelection();
			void insertImages(pastedImages);
			return;
		}
		const html = event.clipboardData.getData('text/html');
		if (!html) return; // plain text: let the browser insert it
		event.preventDefault();
		exec('insertHTML', sanitizeComposedHtml(html));
	};

	const button = (label: string, icon: React.ReactNode, onClick: () => void, isActive = false): React.ReactElement => (
		<button
			type="button"
			className={clsx(styles.toolbarButton, isActive && styles.toolbarButtonActive)}
			title={label}
			aria-label={label}
			aria-pressed={isActive}
			onMouseDown={(event) => event.preventDefault()}
			onClick={onClick}
			data-flx="konvo-mail.editor.toolbar-button"
		>
			{icon}
		</button>
	);

	const toolbar = showToolbar ? (
		<div
			className={clsx(styles.toolbar, toolbarPlacement === 'bottom' && styles.toolbarBottom, toolbarClassName)}
			role="toolbar"
			data-flx="konvo-mail.editor.toolbar"
		>
			{button(i18n._(BOLD_DESCRIPTOR), <TextBIcon size={16} weight="bold" />, () => exec('bold'), active['bold'])}
			{button(i18n._(ITALIC_DESCRIPTOR), <TextItalicIcon size={16} />, () => exec('italic'), active['italic'])}
			{button(
				i18n._(UNDERLINE_DESCRIPTOR),
				<TextUnderlineIcon size={16} />,
				() => exec('underline'),
				active['underline'],
			)}
			{button(
				i18n._(STRIKE_DESCRIPTOR),
				<TextStrikethroughIcon size={16} />,
				() => exec('strikeThrough'),
				active['strikeThrough'],
			)}
			<span className={styles.toolbarSep} />
			<select
				className={styles.toolbarSelect}
				aria-label={i18n._(FONT_SIZE_DESCRIPTOR)}
				defaultValue="3"
				onMouseDown={saveSelection}
				onChange={(event) => {
					restoreSelection();
					exec('fontSize', event.target.value);
				}}
				data-flx="konvo-mail.editor.font-size"
			>
				{FONT_SIZES.map((size) => (
					<option key={size.value} value={size.value}>
						{size.label}
					</option>
				))}
			</select>
			{button(
				i18n._(TEXT_COLOUR_DESCRIPTOR),
				<TextColorIcon size={16} />,
				() => openColours('foreColor'),
				colourMode === 'foreColor',
			)}
			{button(
				i18n._(HIGHLIGHT_DESCRIPTOR),
				<HighlighterIcon size={16} />,
				() => openColours('hiliteColor'),
				colourMode === 'hiliteColor',
			)}
			<span className={styles.toolbarSep} />
			{button(
				i18n._(BULLETS_DESCRIPTOR),
				<ListBulletsIcon size={16} />,
				() => exec('insertUnorderedList'),
				active['insertUnorderedList'],
			)}
			{button(
				i18n._(NUMBERS_DESCRIPTOR),
				<ListNumbersIcon size={16} />,
				() => exec('insertOrderedList'),
				active['insertOrderedList'],
			)}
			{button(i18n._(OUTDENT_DESCRIPTOR), <TextOutdentIcon size={16} />, () => exec('outdent'))}
			{button(i18n._(INDENT_DESCRIPTOR), <TextIndentIcon size={16} />, () => exec('indent'))}
			<span className={styles.toolbarSep} />
			{button(i18n._(ALIGN_LEFT_DESCRIPTOR), <TextAlignLeftIcon size={16} />, () => exec('justifyLeft'))}
			{button(i18n._(ALIGN_CENTER_DESCRIPTOR), <TextAlignCenterIcon size={16} />, () => exec('justifyCenter'))}
			{button(i18n._(ALIGN_RIGHT_DESCRIPTOR), <TextAlignRightIcon size={16} />, () => exec('justifyRight'))}
			<span className={styles.toolbarSep} />
			{button(i18n._(QUOTE_DESCRIPTOR), <QuotesIcon size={16} />, () => exec('formatBlock', 'blockquote'))}
			{button(i18n._(LINK_DESCRIPTOR), <LinkIcon size={16} />, openLink, linkOpen)}
			{allowImages
				? button(i18n._(IMAGE_DESCRIPTOR), <ImageIcon size={16} />, () => {
						saveSelection();
						imageInputRef.current?.click();
					})
				: null}
			{button(i18n._(CLEAR_DESCRIPTOR), <EraserIcon size={16} />, () => {
				exec('removeFormat');
				exec('formatBlock', 'div');
			})}
		</div>
	) : null;

	const surface = (
		<div
			ref={setEditorRef}
			className={clsx(styles.editor, chromeless && styles.editorPlain, surfaceClassName)}
			style={{minHeight}}
			contentEditable
			suppressContentEditableWarning
			tabIndex={0}
			role="textbox"
			aria-multiline="true"
			aria-label={placeholder}
			data-placeholder={placeholder}
			data-empty={empty ? 'true' : undefined}
			spellCheck
			onInput={emit}
			onBlur={emit}
			onKeyUp={refreshActive}
			onMouseUp={refreshActive}
			onKeyDown={handleKeyDown}
			onPaste={handlePaste}
			data-flx="konvo-mail.editor.surface"
		/>
	);

	return (
		<div className={clsx(styles.editorWrap, chromeless && styles.editorWrapPlain)} data-flx="konvo-mail.editor.wrap">
			{toolbarPlacement === 'top' ? toolbar : null}
			{linkOpen ? (
				<div className={styles.linkRow} data-flx="konvo-mail.editor.link-row">
					<input
						ref={linkInputRef}
						className={styles.linkInput}
						value={linkUrl}
						placeholder={i18n._(LINK_PLACEHOLDER_DESCRIPTOR)}
						onChange={(event) => setLinkUrl(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								event.preventDefault();
								applyLink();
							} else if (event.key === 'Escape') {
								setLinkOpen(false);
							}
						}}
						data-flx="konvo-mail.editor.link-input"
					/>
					<button
						type="button"
						className={styles.linkButton}
						onClick={applyLink}
						data-flx="konvo-mail.editor.link-apply"
					>
						{i18n._(LINK_APPLY_DESCRIPTOR)}
					</button>
					<button
						type="button"
						className={styles.linkButton}
						onClick={removeLink}
						data-flx="konvo-mail.editor.link-remove"
					>
						{i18n._(LINK_REMOVE_DESCRIPTOR)}
					</button>
				</div>
			) : null}
			{colourMode ? (
				<div className={styles.colourRow} role="group" data-flx="konvo-mail.editor.colour-row">
					{(colourMode === 'foreColor' ? TEXT_COLOURS : HIGHLIGHT_COLOURS).map((colour) => (
						<button
							key={colour}
							type="button"
							className={styles.colourSwatch}
							style={{'--swatch': colour} as React.CSSProperties}
							aria-label={colour}
							title={colour}
							onMouseDown={(event) => event.preventDefault()}
							onClick={() => applyColour(colour)}
							data-flx="konvo-mail.editor.colour-swatch"
						/>
					))}
					<button
						type="button"
						className={clsx(styles.colourSwatch, styles.colourSwatchNone)}
						aria-label={i18n._(NO_COLOUR_DESCRIPTOR)}
						title={i18n._(NO_COLOUR_DESCRIPTOR)}
						onMouseDown={(event) => event.preventDefault()}
						onClick={() => applyColour(null)}
						data-flx="konvo-mail.editor.colour-none"
					/>
				</div>
			) : null}
			{allowImages ? (
				<input
					ref={imageInputRef}
					type="file"
					accept="image/png,image/jpeg,image/gif,image/webp"
					multiple
					hidden
					onChange={(event) => {
						void insertImages(Array.from(event.target.files ?? []));
						event.target.value = '';
					}}
					data-flx="konvo-mail.editor.image-input"
				/>
			) : null}
			{imageError ? (
				<div className={styles.editorNotice} role="status">
					{i18n._(IMAGE_TOO_LARGE_DESCRIPTOR)}
				</div>
			) : null}
			{overlay ? (
				<div ref={setStage} className={styles.editorStage} data-flx="konvo-mail.editor.stage">
					{surface}
					{stage && editorElement ? overlay({editor: editorElement, stage}) : null}
				</div>
			) : (
				surface
			)}
			{toolbarPlacement === 'bottom' ? toolbar : null}
		</div>
	);
};
